import pptxgen from "pptxgenjs";
import { Injectable } from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class DownloadDashboard {
  public errorWhilegenerating = false;

  public async downloadDashboardReport(reports: any[],otherFilters: string, clientLogo: string) {
    try {
      let pptx = new pptxgen();

      let clientLogoBase64 = "";

      try {
        if (clientLogo) {
          clientLogoBase64 = await this.urlToBase64(clientLogo);
        }
      } catch (err) {
        console.error("Failed to load client logo", err);
      }
      // ─────────────────────────────────────────────────────────────
      // SLIDE 1: OVERALL SUMMARY (ONLY ONCE)
      // ─────────────────────────────────────────────────────────────

      if (reports && reports.length > 0) {
        const firstReport = reports[0];
        const total = firstReport.total;

        let slide = pptx.addSlide();

        // SLIDE BACKGROUND
        slide.addShape(pptx.ShapeType.rect, {
          x: 0,
          y: 0,
          w: "100%",
          h: "100%",
          fill: { color: "F0F4FA" },
          line: { type: "none" },
        });

        // TOP HEADER BAR
        slide.addShape(pptx.ShapeType.rect, {
          x: 0,
          y: 0,
          w: "100%",
          h: 0.75,
          fill: "0757A7",
          line: { type: "none" },
        });

        // HRA LOGO
        slide.addImage({
          path: "assets/hra-logo.png",
          x: 0.15,
          y: 0.08,
          w: 1.2,
          h: 0.5,
        });

        // SURVEY NAME
        slide.addText(`SURVEY : ${firstReport.surveyName} - OVERALL`, {
          x: 1.9,
          y: 0.1,
          w: 5.8,
          h: 0.55,
          fontSize: 15,
          bold: true,
          fontFace: "Poppins",
          color: "FFFFFF",
          valign: "middle",
          align: "center",
        });

        // CLIENT LOGO
        if (clientLogoBase64) {
          slide.addImage({
            data: clientLogoBase64,
            x: 8.45,
            y: 0.08,
            w: 1.2,
            h: 0.5,
          });
        }

        const chartData = [
          {
            name: "Status",
            labels: ["Pending", "Completed"],
            values: [total.pending, total.completed],
          },
        ];

        // PIE CHART CARD
        slide.addShape(pptx.ShapeType.rect, {
          x: 0.4,
          y: 0.9,
          w: 5.2,
          h: 4.2,
          fill: { color: "FFFFFF" },
          line: { color: "D9D9D9", width: 1 },
          shadow: {
            type: "outer",
            blur: 5,
            offset: 2,
            angle: 45,
            color: "CCCCCC",
          },
        });

        slide.addChart(pptx.ChartType.pie, chartData, {
          x: 0.5,
          y: 1.0,
          w: 5.0,
          h: 4.0,
          showLegend: true,
          legendPos: "t",
          legendFontSize: 12,
          dataLabelPosition: "ctr",
          showPercent: true,
          showValue: false,
          dataLabelFormatCode: "0.00%",
          dataLabelFontSize: 13,
          dataLabelFontBold: true,
          dataLabelColor: "FFFFFF",
          chartColors: ["E0542C", "70C4FC"],
          dataBorder: { pt: 2, color: "FFFFFF" },
        });

        // RIGHT PANEL CARD
        slide.addShape(pptx.ShapeType.rect, {
          x: 5.8,
          y: 0.9,
          w: 3.9,
          h: 4.2,
          fill: { color: "FFFFFF" },
          line: { color: "D9D9D9", width: 1 },
          shadow: {
            type: "outer",
            blur: 5,
            offset: 2,
            angle: 45,
            color: "CCCCCC",
          },
        });

        // PANEL TITLE BAR
        slide.addShape(pptx.ShapeType.rect, {
          x: 5.8,
          y: 0.9,
          w: 3.9,
          h: 0.5,
          fill: "0757A7",
          line: { type: "none" },
        });

        slide.addText("Overall Analysis", {
          x: 5.9,
          y: 0.92,
          w: 3.7,
          h: 0.45,
          fontSize: 14,
          fontFace: "Poppins",
          bold: true,
          color: "FFFFFF",
          valign: "middle",
        });

        // TOTAL
        slide.addText("Total Participants", {
          x: 5.9,
          y: 1.4,
          fontSize: 11,
          fontFace: "Poppins",
          color: "888888",
           italic: true,
        });

        // DIVIDER
        slide.addShape(pptx.ShapeType.line, {
            x: 5.9, y: 1.75, w: 3.5, h: 0,
            line: { color: "E0E0E0", width: 1 }
        });

        slide.addText(`${total.total}`, {
          x: 5.9,
          y: 1.9,
          fontSize: 28,
          fontFace: "Poppins",
          bold: true,
          color: "0757A7",
        });

        // ── COMPLETED PROGRESS BAR ──────────────────────────────
        const completedPercent =
          total.total > 0 ? (total.completed / total.total) * 100 : 0;
        const pendingPercent =
          total.total > 0 ? (total.pending / total.total) * 100 : 0;

        const barStartX = 5.9;
        const barMaxWidth = 2.2; // max track width in inches
        const barHeight = 0.28;
        const labelX = 8.2; // x for "Completed : 13" text
        const labelW = 1.45;

        // ── COMPLETED ──
        slide.addShape(pptx.ShapeType.rect, {
          // gray track
          x: barStartX,
          y: 2.5,
          w: barMaxWidth,
          h: barHeight,
          fill: { color: "FFFFFF" },
          line: { type: "none" },
        });
        slide.addShape(pptx.ShapeType.rect, {
          // filled (blue)
          x: barStartX,
          y: 2.5,
          w: Math.max((completedPercent / 100) * barMaxWidth, 0.01),
          h: barHeight,
          fill: { color: "70C4FC" },
          line: { type: "none" },
        });
        slide.addText("Completed: ", {
          x: labelX,
          y: 2.48,
          w: 1.1,
          h: 0.3,
          fontFace: "Poppins",
          fontSize: 11,
          color: "555555",
          align: "right",
        });
        slide.addText(`${total.completed}`, {
          x: labelX + 0.4,
          y: 2.48,
          w: 1,
          h: 0.3,
          fontFace: "Poppins",
          fontSize: 11,
          bold: true,
          color: "000000",
          align: "right",
        });

        // ── PENDING ──
        slide.addShape(pptx.ShapeType.rect, {
          // gray track
          x: barStartX,
          y: 3.0,
          w: barMaxWidth,
          h: barHeight,
          fill: { color: "FFFFFF" },
          line: { type: "none" },
        });
        slide.addShape(pptx.ShapeType.rect, {
          // filled (orange-red)
          x: barStartX,
          y: 3.0,
          w: Math.max((pendingPercent / 100) * barMaxWidth, 0.01),
          h: barHeight,
          fill: { color: "E0542C" },
          line: { type: "none" },
        });
        slide.addText("Pending: ", {
          x: labelX,
          y: 2.98,
          w: 1.1,
          h: 0.3,
          fontFace: "Poppins",
          fontSize: 11,
          color: "555555",
          align: "right",
        });
        slide.addText(`${total.pending}`, {
          x: labelX + 0.4,
          y: 2.98,
          w: 1,
          h: 0.3,
          fontFace: "Poppins",
          fontSize: 11,
          bold: true,
          color: "000000",
          align: "right",
        });

        // DIVIDER
        slide.addShape(pptx.ShapeType.line, {
          x: 5.9,
          y: 3.55,
          w: 3.5,
          h: 0,
          line: { color: "E0E0E0", width: 1 },
        });

        // COMPLETION RATE
        slide.addText("Completion Rate", {
          x: 5.9,
          y: 3.72,
          fontFace: "Poppins",
          fontSize: 11,
          color: "888888",
        });
        slide.addText(`${total.completionRate}%`, {
          x: 5.9,
          y: 4.05,
          fontFace: "Poppins",
          fontSize: 26,
          bold: true,
          color: "000000",
        });
        // FOOTER BAR
        slide.addShape(pptx.ShapeType.rect, {
          x: 0,
          y: 5.37,
          w: "100%",
          h: 0.25,
          fill: "0757A7",
          line: { type: "none" },
        });
      }

      // ─────────────────────────────────────────────────────────────
      // FILTER TABLE SLIDES
      // ─────────────────────────────────────────────────────────────

      reports.forEach((report) => {
        const isFilterApplied = report.data && report.data.length > 0;

        if (isFilterApplied) {
          const header = report.header;
          const rowsPerSlide = 10;
          const startX = 0.3;
          const tableY = 1.0;
          const startY = tableY;
          const headerHeight = 0.45;
          const rowHeight = 0.42;
          const paddingLeft = 0.15;
          const completionColX = startX + 0.5 + 2.8 + paddingLeft;

          for (let i = 0; i < report.data.length; i += rowsPerSlide) {
            let slide = pptx.addSlide();

            // BACKGROUND
            slide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: 0,
              w: "100%",
              h: "100%",
              fill: { color: "F0F4FA" },
              line: { type: "none" },
            });

            // HEADER BAR
            slide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: 0,
              w: "100%",
              h: 0.75,
              fill: "0757A7",
              line: { type: "none" },
            });

            // HRA LOGO
            slide.addImage({
              path: "assets/hra-logo.png",
              x: 0.15,
              y: 0.08,
              w: 1.2,
              h: 0.5,
            });

            slide.addText(`Survey : ${report.surveyName} - ${header.filter}`, {
              x: 1.9,
              y: 0.1,
              w: 5.8,
              h: 0.55,
              fontSize: 15,
              bold: true,
              fontFace: "Poppins",
              color: "FFFFFF",
              valign: "middle",
              align: "center",
            });

            // CLIENT LOGO
            if (clientLogoBase64) {
              slide.addImage({
                data: clientLogoBase64,
                x: 8.45,
                y: 0.08,
                w: 1.2,
                h: 0.5,
              });
            }

            const tableRows: any[] = [];

            const headerCellOpts: any = {
              bold: true,
              color: "FFFFFF",
              fill: "0757A7",
              align: "center",
              fontFace: "Poppins",
              valign: "middle",
              fontSize: 11,
            };

            tableRows.push([
              { text: "#", options: headerCellOpts },
              { text: header.filter || "Filter", options: headerCellOpts },
              { text: "Completion Rate", options: headerCellOpts },
              { text: "Completed", options: headerCellOpts },
              { text: "Pending", options: headerCellOpts },
              { text: "Total", options: headerCellOpts },
            ]);

            const chunk = report.data.slice(i, i + rowsPerSlide);

            chunk.forEach((row: any, index: number) => {
              const rowFill = index % 2 === 0 ? "FFFFFF" : "EDF2FB";

              const dataCellOpts: any = {
                align: "center",
                valign: "middle",
                fontSize: 10,
                fill: rowFill,
              };

              tableRows.push([
                { text: (i + index + 1).toString(), options: dataCellOpts },
                { text: row.filter, options: { ...dataCellOpts } },
                { text: "", options: dataCellOpts },
                { text: row.completed.toString(), options: dataCellOpts },
                { text: row.pending.toString(), options: dataCellOpts },
                { text: row.total.toString(), options: dataCellOpts },
              ]);
            });

            slide.addTable(tableRows, {
              x: startX,
              y: tableY,
              w: 9.4,
              colW: [0.5, 3, 3, 1, 0.95, 0.95],
              rowH: 0.38,
              border: {
                type: "solid",
                pt: 0.5,
                color: "D0D8E8",
              },
              fontFace: "Poppins",
              fontSize: 11,
            });

            // PROGRESS BARS
            chunk.forEach((row: any, index: number) => {
              const percent = Number(row.completionRate) || 0;

              const barY =
                startY +
                headerHeight +
                index * rowHeight -
                -0.07 -
                index * 0.045;

              const colWidth = 3;
              const barWidth = 2.5;
              const filledWidth = (percent / 100) * barWidth;
              const barX = completionColX + (colWidth - barWidth) / 2;

              slide.addShape(pptx.ShapeType.rect, {
                x: barX,
                y: barY,
                w: barWidth,
                h: 0.18,
                fill: { color: "E0E0E0" },
                line: { type: "none" },
              });

              slide.addShape(pptx.ShapeType.rect, {
                x: barX,
                y: barY,
                w: Math.max(filledWidth, 0),
                h: 0.18,
                fill: { color: "70c4fc" },
                line: { type: "none" },
              });

              slide.addText(percent + "%", {
                x: barX,
                y: barY - 0.07,
                w: barWidth,
                h: 0.3,
                align: "center",
                fontSize: 10,
                bold: true,
                color: "333333",
              });
            });

            // ─────────────────────────────────────────────
            // "OTHERS" NOTE (mirrors DownloadEngagementDashboard)
            // Only show on the page where an "Others" row actually appears.
            // ─────────────────────────────────────────────
            const hasOthersOnThisPage = chunk.some(
              (row: any) =>
                (row.filter || "").trim().toLowerCase() === "others",
            );

            if (hasOthersOnThisPage && otherFilters) {
              const currentFilter = (header.filter || "").trim().toLowerCase();

              const filterNote = otherFilters.split("||").find((item) => {
                const key = item.split(":")[0].trim().toLowerCase();
                return key === currentFilter;
              });

              if (filterNote) {
                const parts = filterNote.split(":");

                const key = parts[0].trim();
                const value = parts.slice(1).join(":").trim();

                slide.addText(`Note: Other ${key} consists of - ${value}`, {
                  x: 0.3,
                  y: 4.95,
                  w: 9.4,
                  h: 0.3,
                  fontSize: 8,
                  fontFace: "Poppins",
                  color: "F28C00",
                  bold: true,
                });
              }
            }

            slide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: 5.37,
              w: "100%",
              h: 0.25,
              fill: "0757A7",
              line: { type: "none" },
            });
          }
        }
      });

      const filterNames = reports
        .map((r) => r.header.filter)
        .filter(Boolean)
        .join(", ");

      const fileName = `${reports[0].surveyName || "Survey"} - ${filterNames} - Dashboard Report`;

      pptx.writeFile(fileName + ".pptx");

      return this.errorWhilegenerating;
    } catch (error) {
      console.error(error);

      this.errorWhilegenerating = true;

      return this.errorWhilegenerating;
    }
  }

  private async urlToBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        resolve(reader.result as string);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}